import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Upload, Trash2, Loader2, FileText, File, Image, FileArchive } from 'lucide-react';

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf')) return FileText;
  if (contentType?.includes('zip') || contentType?.includes('archive')) return FileArchive;
  return File;
}

export function ProjectFilesTab({
  files, onFileUpload, onDeleteFile, uploading, fileInputRef,
  canUpload, canDelete, formatDate,
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Project Files</h3>
        {canUpload && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={onFileUpload}
              className="hidden"
              data-testid="file-upload-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary"
              data-testid="upload-file-btn"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload File
            </Button>
          </>
        )}
      </div>

      {(!files || files.length === 0) ? (
        <Card>
          <CardContent className="empty-state py-12">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No files uploaded</h3>
            <p className="text-sm text-muted-foreground">
              Upload project plans and documents here
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header">File</TableHead>
                  <TableHead className="table-header">Type</TableHead>
                  <TableHead className="table-header">Uploaded</TableHead>
                  {canDelete && (
                    <TableHead className="table-header text-right">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((file) => {
                  const FileIcon = getFileIcon(file.content_type);
                  return (
                    <TableRow key={file.file_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-muted">
                            <FileIcon className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <span className="font-medium">{file.filename}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {file.content_type || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(file.uploaded_at)}
                      </TableCell>
                      {canDelete && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => onDeleteFile(file.file_id)}
                            data-testid={`delete-file-${file.file_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
